import { Tag, TreeSelect, TreeSelectProps } from 'antd'
import { TreeNode } from 'antd/es/tree-select'
import IconDown from 'components/icons/IconDown'
import { EPeriodType } from 'enums/common'
import { useRef } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { cn } from 'utils/common'

interface TreeNodeData {
  title: string
  key: string
  value: string
  children?: TreeNodeData[]
}

interface IProps<T extends FieldValues>
  extends Pick<TreeSelectProps, 'suffixIcon' | 'className' | 'defaultValue' | 'onChange' | 'value' | 'treeCheckable'> {
  label?: string
  control?: Control<T>
  placeholder?: string
  name: Path<T>
  helperText?: string
  treeData: TreeNodeData[]
}

const handleRender = (props: any, value: string[]) => {
  const { label, closable, onClose } = props
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }
  return (
    <Tag
      className='text-sm font-medium text-neutral-700 bg-white border-[0.5px] border-neutral-border-light py-[1px] px-1 me-1'
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={value?.length > 1 && onClose}
    >
      {label}
    </Tag>
  )
}

export default function BaseTreeSelect<T extends FieldValues = FieldValues>(props: IProps<T>) {
  const { label, placeholder, className, ...rest } = props

  const prevRef = useRef<string>('')

  const renderTreeNodes = (data: TreeNodeData[], values: string[]) =>
    data.map((item) => {
      if (item.children) {
        return (
          <TreeNode title={item.title} key={item.key} dataRef={item} value={item.value}>
            {renderTreeNodes(item.children, values)}
          </TreeNode>
        )
      }

      return <TreeNode title={item.title} value={item.value} dataRef={item} key={item.key}></TreeNode>
    })

  return (
    <>
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            {label && <label className='text-sm text-neutral-700'>{label}</label>}
            <TreeSelect
              tagRender={(props) => handleRender(props, value)}
              className={cn(
                'w-full h-10 [&>*]:pl-2 [&>*]:text-base [&>*]:font-semibold [&>*]:text-neutral-700 [&>div]:bg-neutral-600 [&>*]:border-0',
                { 'mt-1': label },
                className
              )}
              onChange={(value, label, extra) => {
                if ([EPeriodType.OVERALL, EPeriodType.QUARTERLY].includes(extra.triggerValue as EPeriodType)) {
                  if (value.length && prevRef.current !== extra.triggerValue) {
                    prevRef.current = extra.triggerValue as string
                    const preLength = extra.preValue.length
                    const newValue = value.slice(preLength)
                    onChange(newValue)
                    return
                  }
                } else {
                  prevRef.current = extra.triggerValue as string
                  if (value.length <= 0) {
                    onChange(extra.preValue.map((v) => v.value))
                    return
                  }
                  const index = value.findIndex((v: EPeriodType) =>
                    [EPeriodType.OVERALL, EPeriodType.QUARTERLY].includes(v)
                  )
                  if (index != -1) {
                    value.splice(index, 1)
                  }
                  onChange(value)
                  return
                }
              }}
              value={value}
              suffixIcon={<IconDown />}
              placeholder={placeholder}
              maxTagCount={'responsive'}
              {...rest}
            >
              {props.treeData && renderTreeNodes(props.treeData, value)}
            </TreeSelect>
            {error && <div className='text-error text-sm pt-1'>{error.message}</div>}
          </>
        )}
      />
    </>
  )
}
